import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const Pagination = ({ currentPage, totalPages, totalCount, perPage, onPageChange }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';

  if (totalPages <= 1) return null;

  const from = (currentPage - 1) * perPage + 1;
  const to = Math.min(currentPage * perPage, totalCount);

  // Build page numbers: show first, last, current ± 1, with ellipsis
  const pages = [];
  const addPage = (p) => { if (!pages.includes(p) && p >= 1 && p <= totalPages) pages.push(p); };

  addPage(1);
  if (currentPage > 3) pages.push('...');
  addPage(currentPage - 1);
  addPage(currentPage);
  addPage(currentPage + 1);
  if (currentPage < totalPages - 2) pages.push('...');
  addPage(totalPages);

  // Dedupe and sort (keep ellipsis in place)
  const uniquePages = [];
  let lastNum = 0;
  for (const p of pages) {
    if (p === '...') {
      if (uniquePages[uniquePages.length - 1] !== '...') uniquePages.push(p);
    } else if (p > lastNum) {
      uniquePages.push(p);
      lastNum = p;
    }
  }

  const btnBase = `min-w-[32px] h-8 flex items-center justify-center rounded-lg text-[13px] font-rasa font-medium transition-colors`;
  const btnActive = d ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white';
  const btnInactive = d ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100';
  const btnDisabled = 'opacity-30 cursor-not-allowed';

  return (
    <div className={`flex items-center justify-between px-4 py-3 border-t ${d ? 'border-gray-700' : 'border-gray-200'}`}>
      <span className={`text-[13px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>
        {from}–{to} of {totalCount}
      </span>
      <div className="flex items-center gap-0.5">
        {/* Previous */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className={`${btnBase} px-1 ${currentPage <= 1 ? btnDisabled : btnInactive}`}
        >
          <ChevronLeft size={16} />
        </button>

        {/* Page numbers */}
        {uniquePages.map((p, i) =>
          p === '...' ? (
            <span key={`e${i}`} className={`px-1 text-[13px] ${d ? 'text-gray-600' : 'text-gray-400'}`}>...</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`${btnBase} ${currentPage === p ? btnActive : btnInactive}`}
            >
              {p}
            </button>
          )
        )}

        {/* Next */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className={`${btnBase} px-1 ${currentPage >= totalPages ? btnDisabled : btnInactive}`}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
